import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { installationApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  User,
  CheckCircle,
  Clock,
  Play,
  Trash2,
  Package,
  Paperclip,
  Upload,
  Mail,
  FileText,
  X,
  Download,
  FileDown
} from 'lucide-react';
import { format } from 'date-fns';

const statusConfig = {
  scheduled: { label: 'Scheduled', class: 'bg-blue-100 text-blue-800 border border-blue-200', icon: Clock },
  in_progress: { label: 'In Progress', class: 'bg-yellow-100 text-yellow-800 border border-yellow-200', icon: Play },
  completed: { label: 'Completed', class: 'bg-green-100 text-green-800 border border-green-200', icon: CheckCircle },
};

const statusFlow = ['scheduled', 'in_progress', 'completed'];

export default function ViewInstallation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isInstaller } = useAuth();
  const [installation, setInstallation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState('job_sheet');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const fileInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const res = await installationApi.getById(id);
      setInstallation(res.data);
    } catch (error) {
      console.error('Error fetching installation:', error);
      toast.error('Failed to load installation');
      navigate('/installations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await installationApi.updateStatus(id, { status: newStatus });
      toast.success(`Installation ${newStatus === 'completed' ? 'completed' : 'updated'} successfully`);
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      await installationApi.uploadAttachment(id, file, selectedFileType);
      toast.success('Attachment uploaded successfully');
      fetchData();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload attachment');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await installationApi.deleteAttachment(id, attachmentId);
      toast.success('Attachment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const res = await installationApi.getAttachment(id, attachment.id);
      const data = res.data.data;
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: res.data.content_type });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  const handleSendEmail = async () => {
    if (!installation.installer_email) {
      toast.error('Installer has no email address configured');
      return;
    }
    
    setSendingEmail(true);
    try {
      await installationApi.sendEmail(id);
      toast.success('Email sent to installer successfully');
      fetchData();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await installationApi.downloadPdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${installation.installation_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDelete = async () => {
    try {
      await installationApi.delete(id);
      toast.success('Installation deleted');
      navigate('/installations');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getNextStatus = () => {
    if (!installation) return null;
    const currentIndex = statusFlow.indexOf(installation.status);
    if (currentIndex < statusFlow.length - 1) {
      return statusFlow[currentIndex + 1];
    }
    return null;
  };

  const getNextStatusAction = () => {
    const next = getNextStatus();
    switch (next) {
      case 'in_progress': return 'Start Installation';
      case 'completed': return 'Mark as Completed';
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading installation...</div>
      </div>
    );
  }

  if (!installation) {
    return null;
  }

  const StatusIcon = statusConfig[installation.status]?.icon || Clock;

  return (
    <div className="space-y-6" data-testid="view-installation-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/installations')} className="rounded-sm">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-semibold font-heading font-mono-data">{installation.installation_number}</h1>
              <Badge className={`${statusConfig[installation.status]?.class} rounded-none text-xs uppercase tracking-wider`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusConfig[installation.status]?.label}
              </Badge>
              {installation.email_sent && (
                <Badge variant="outline" className="rounded-none text-xs">
                  <Mail className="w-3 h-3 mr-1" />
                  Email Sent
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {installation.customer_name}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            size="sm" 
            className="rounded-sm"
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            data-testid="btn-download-pdf"
          >
            <FileDown className="w-4 h-4 mr-2" />
            {downloadingPdf ? 'Downloading...' : 'Download PDF'}
          </Button>
          {installation.status === 'scheduled' && !isInstaller && (
            <>
              <Button 
                variant="outline"
                size="sm" 
                className="rounded-sm"
                onClick={handleSendEmail}
                disabled={sendingEmail || !installation.installer_email}
                data-testid="btn-send-email"
              >
                <Mail className="w-4 h-4 mr-2" />
                {sendingEmail ? 'Sending...' : 'Send to Installer'}
              </Button>
              <Button 
                variant="destructive" 
                size="sm" 
                className="rounded-sm"
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="btn-delete-installation"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </>
          )}
          {getNextStatusAction() && (
            <Button 
              className="rounded-sm"
              onClick={() => handleStatusUpdate(getNextStatus())}
              disabled={updating}
              data-testid="btn-update-status"
            >
              {getNextStatusAction()}
            </Button>
          )}
        </div>
      </div>

      {/* Status Progress */}
      <Card className="border border-border shadow-none rounded-sm">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            {statusFlow.map((status, index) => {
              const currentIndex = statusFlow.indexOf(installation.status);
              const isCompleted = index < currentIndex;
              const isCurrent = index === currentIndex;
              const Icon = statusConfig[status]?.icon || Clock;
              
              return (
                <div key={status} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                      isCompleted ? 'bg-green-500 border-green-500 text-white' :
                      isCurrent ? 'border-primary bg-primary/10 text-primary' :
                      'border-muted bg-muted text-muted-foreground'
                    }`}>
                      {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={`text-xs mt-2 text-center ${isCurrent ? 'font-medium text-primary' : 'text-muted-foreground'}`}>
                      {statusConfig[status]?.label}
                    </span>
                  </div>
                  {index < statusFlow.length - 1 && (
                    <div className={`h-0.5 flex-1 -mt-6 ${isCompleted ? 'bg-green-500' : 'bg-muted'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Attachments */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="border-b border-border/50 pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Paperclip className="w-5 h-5" />
                  Attachments
                </CardTitle>
                {!isInstaller && installation.status === 'scheduled' && (
                  <div className="flex items-center gap-2">
                    <Select value={selectedFileType} onValueChange={setSelectedFileType}>
                      <SelectTrigger className="w-[140px] h-8 rounded-sm text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="job_sheet">Job Sheet</SelectItem>
                        <SelectItem value="packing_slip">Packing Slip</SelectItem>
                      </SelectContent>
                    </Select>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm h-8"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="btn-upload-attachment"
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {installation.attachments?.length > 0 ? (
                <div className="space-y-2">
                  {installation.attachments.map((att) => (
                    <div 
                      key={att.id} 
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-sm"
                      data-testid={`attachment-${att.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{att.filename}</p>
                          <p className="text-xs text-muted-foreground capitalize">
                            {att.file_type?.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDownloadAttachment(att)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {!isInstaller && installation.status === 'scheduled' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDeleteAttachment(att.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  No attachments yet. Upload job sheet and packing slip.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Materials */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Package className="w-5 h-5" />
                Materials Used
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {installation.materials?.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table data-testid="materials-table">
                    <TableHeader>
                      <TableRow className="bg-muted/50 hover:bg-muted/50">
                        <TableHead className="font-medium">Product</TableHead>
                        <TableHead className="font-medium text-right">Quantity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {installation.materials.map((material) => (
                        <TableRow key={material.product_id || material.product_name}>
                          <TableCell className="font-medium">{material.product_name}</TableCell>
                          <TableCell className="text-right font-mono-data">{material.quantity}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="p-6 text-center text-muted-foreground">
                  No materials recorded
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {installation.notes && (
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-base font-heading">Notes</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{installation.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Details */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle className="text-base font-heading">Customer</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div>
                <p className="font-medium">{installation.customer_name}</p>
              </div>
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{installation.customer_address}</span>
              </div>
            </CardContent>
          </Card>

          {/* Schedule Details */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle className="text-base font-heading">Schedule</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>{installation.installation_date ? format(new Date(installation.installation_date), 'PPP') : '-'}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <div>
                  <span>{installation.installer_name}</span>
                  {installation.installer_email && (
                    <p className="text-xs text-muted-foreground">{installation.installer_email}</p>
                  )}
                </div>
              </div>
              {installation.po_number && (
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-muted-foreground" />
                  <Link to={`/purchase-orders/${installation.po_id}`} className="text-primary hover:underline">
                    {installation.po_number}
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timestamps */}
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle className="text-base font-heading">Timeline</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{installation.created_at ? format(new Date(installation.created_at), 'PPP') : '-'}</span>
              </div>
              {installation.email_sent_at && (
                <div className="flex justify-between text-blue-600">
                  <span>Email Sent</span>
                  <span>{format(new Date(installation.email_sent_at), 'PPP p')}</span>
                </div>
              )}
              {installation.started_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Started</span>
                  <span>{format(new Date(installation.started_at), 'PPP p')}</span>
                </div>
              )}
              {installation.completed_at && (
                <div className="flex justify-between text-green-600">
                  <span>Completed</span>
                  <span>{format(new Date(installation.completed_at), 'PPP p')}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Installation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {installation.installation_number}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
